import { debugLog } from "../utils/debug";
import { REGISTRATION_BASE_URL } from "../config/environment";
import { getImpersonationHeaders } from "../context/ImpersonationContext";

const USFS_BASE_URL = `${REGISTRATION_BASE_URL}/usfs`;

class USFSService {
  constructor() {
    this.baseUrl = USFS_BASE_URL;
  }

  async makeRequest(endpoint, options = {}, token) {
    const url = `${this.baseUrl}${endpoint}`;

    // Get impersonation headers if impersonation is active
    const impersonationHeaders = getImpersonationHeaders();

    const config = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...impersonationHeaders,
        ...options.headers,
      },
      ...options,
    };

    debugLog(`USFS ${config.method} request:`, url);
    if (impersonationHeaders['X-Impersonate-User']) {
      debugLog('🎭 Impersonating user:', impersonationHeaders['X-Impersonate-User']);
    }

    const response = await fetch(url, config);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async linkAccount({ username, password, consent, autoSync = true }, token) {
    return this.makeRequest(
      "/link",
      {
        method: "POST",
        body: JSON.stringify({ username, password, consent, autoSync }),
      },
      token
    );
  }

  async getStatus(token) {
    return this.makeRequest("/status", {}, token);
  }

  async triggerManualSync(token) {
    return this.makeRequest(
      "/sync",
      {
        method: "POST",
      },
      token
    );
  }

  async unlinkAccount({ clearData = false } = {}, token) {
    return this.makeRequest(
      "/unlink",
      {
        method: "POST",
        body: JSON.stringify({ clearData }),
      },
      token
    );
  }

  async getSkaterData(token) {
    return this.makeRequest("/skater-data", {}, token);
  }

  // ============================================
  // SKATER-SPECIFIC USFS LINKING METHODS
  // ============================================

  async linkSkaterAccount(skaterId, { username, password, consent }, token) {
    return this.makeRequest(
      `/skater/${skaterId}/link`,
      {
        method: "POST",
        body: JSON.stringify({ username, password, consent }),
      },
      token
    );
  }

  async getSkaterStatus(skaterId, token) {
    return this.makeRequest(`/skater/${skaterId}/status`, {}, token);
  }

  async triggerSkaterSync(skaterId, token) {
    return this.makeRequest(
      `/skater/${skaterId}/sync`,
      {
        method: "POST",
      },
      token
    );
  }

  async unlinkSkaterAccount(skaterId, { clearData = false } = {}, token) {
    return this.makeRequest(
      `/skater/${skaterId}/unlink`,
      {
        method: "POST",
        body: JSON.stringify({ clearData }),
      },
      token
    );
  }

  /**
   * Lookup USFS member data (for populating a new skater form)
   * Does not save anything - just returns the member info
   */
  async lookupMember({ username, password }, token) {
    return this.makeRequest(
      "/lookup",
      {
        method: "POST",
        body: JSON.stringify({ username, password }),
      },
      token
    );
  }
}

const usfsService = new USFSService();

export default usfsService;
